import React, { useEffect, useState } from "react";
import { UserRound } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { PageCta } from "@/components/shared/PageCta";
import { Section } from "@/components/ui-kit/Kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { fetchCommittee } from "@/lib/api";

export default function Committees() {
  const { t } = useI18n();
  const p = t("pages.committees");
  const [members, setMembers] = useState([]);
  useSEO({ title: p.title, description: p.lead, path: "/committees" });

  useEffect(() => {
    let alive = true;
    fetchCommittee()
      .then((d) => alive && setMembers(d.items || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="committees-page">
        <div className="space-y-16">
          {p.groups.map((g, gi) => {
            const list = members.filter((m) => m.group === g.key);
            return (
              <Reveal key={g.key}>
                <div className="border-t border-navy/10 pt-8">
                  <div className="flex flex-wrap items-baseline gap-4">
                    <span className="font-mono text-[11px] tracking-[0.24em] text-grn">
                      {String(gi + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-display text-2xl font-semibold text-navy sm:text-3xl">{g.title}</h2>
                  </div>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">{g.body}</p>

                  {list.length === 0 ? (
                    <div
                      className="mt-7 flex items-center gap-4 border border-dashed border-navy/20 bg-slate-50/60 p-6"
                      data-testid={`committee-empty-${g.key}`}
                    >
                      <UserRound className="h-6 w-6 text-navy/25" strokeWidth={1.3} />
                      <p className="text-[13px] text-slate-500">{t("common.noContentYet")}</p>
                    </div>
                  ) : (
                    <Stagger className="mt-7 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3">
                      {list.map((m, i) => (
                        <StaggerItem key={m.name + i} className="bg-white">
                          <div className="p-7" data-testid={`committee-${g.key}-${i + 1}`}>
                            {m.photo ? (
                              <img src={m.photo} alt={m.name} loading="lazy" className="h-20 w-20 rounded-full object-cover" />
                            ) : (
                              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-navy/5">
                                <UserRound className="h-8 w-8 text-navy/30" strokeWidth={1.3} />
                              </div>
                            )}
                            <h3 className="mt-5 font-display text-lg font-semibold text-navy">{m.name}</h3>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-grn">{m.position}</p>
                            {m.district && <p className="mt-2 text-[12px] text-slate-500">{m.district}</p>}
                            {m.profile && <p className="mt-4 text-sm leading-relaxed text-slate-600">{m.profile}</p>}
                            {m.contact && <p className="mt-3 font-mono text-[11px] text-navy/60">{m.contact}</p>}
                          </div>
                        </StaggerItem>
                      ))}
                    </Stagger>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>
      <PageCta />
    </>
  );
}
